// CSR-2169 — the ONE sync-eligibility rule for every AMS sync entry point.
//
// A carrier "Sync now" can only succeed for (a) a carrier we have a scraper
// for (GEICO/Progressive) and (b) the most recent term of a policy number —
// the carrier portal renders one page per policy number, so older/renewed
// terms structurally 404 and burn a full 7.5-minute poll budget ending in a
// false "Sync did not complete within 8 minutes" (86% of whole-insured sync
// timeouts in production — CSR-2155).
//
// Both the UI buttons (client) and the whole-insured fan-out (server) read
// this module, so what the button shows and what the trigger does can never
// disagree. The future-dated renewal term IS the eligible one (product
// decision, Aug 14): one sync entry point per policy number, on its newest
// term — the scraper refreshes whatever term the carrier currently renders.
import { isCarrierSyncSupported } from "./schema";

export type PolicySyncEligibilityInput = {
  policyNumber?: string | null;
  effectiveDate?: string | null;
  carrier?: string | null;
  parentCarrier?: string | null;
};

export type PolicySyncIneligibleReason = "unsupported_carrier" | "older_term";

// Approved copy (CSR-2169, verbatim — do not reword without product sign-off).
// Tooltips exist only to explain why a disabled button is disabled; an enabled
// sync button carries no tooltip (scope adjustment, Ricardo, Aug 14).
export const POLICY_SYNC_INELIGIBLE_TOOLTIPS: Record<
  PolicySyncIneligibleReason,
  string
> = {
  older_term:
    "This is an older term of this policy. Only the most recent term can be synced.",
  unsupported_carrier: "Automatic sync isn't available for this carrier.",
};
export const INSURED_SYNC_NONE_ELIGIBLE_TOOLTIP =
  "None of this insured's policies can be synced automatically.";

/**
 * Why this policy cannot be synced, or null when it is eligible.
 * `insuredPolicies` is the full set of the insured's policy terms (the list
 * the page/fan-out already holds) — needed to rank terms of the same number.
 */
export function getPolicySyncIneligibleReason(
  policy: PolicySyncEligibilityInput,
  insuredPolicies: PolicySyncEligibilityInput[],
): PolicySyncIneligibleReason | null {
  if (!isCarrierSyncSupported(policy.carrier ?? "", policy.parentCarrier)) {
    return "unsupported_carrier";
  }
  if (!isMostRecentTerm(policy, insuredPolicies)) {
    return "older_term";
  }
  return null;
}

export function isPolicySyncEligible(
  policy: PolicySyncEligibilityInput,
  insuredPolicies: PolicySyncEligibilityInput[],
): boolean {
  return getPolicySyncIneligibleReason(policy, insuredPolicies) === null;
}

// Most recent term by effective date among the terms sharing this policy
// number. ISO dates compare correctly as strings (the same convention as
// `sortPoliciesByEffectiveDate`). Fail-open on the unrankable cases: a policy
// with no number is its own singleton, and a tie on the newest date keeps
// every tied term eligible rather than disabling all of them arbitrarily.
function isMostRecentTerm(
  policy: PolicySyncEligibilityInput,
  insuredPolicies: PolicySyncEligibilityInput[],
): boolean {
  const policyNumber = normalizePolicyNumber(policy.policyNumber);
  if (!policyNumber) return true;
  const newestSiblingDate = insuredPolicies
    .filter((p) => normalizePolicyNumber(p.policyNumber) === policyNumber)
    .reduce(
      (newest, p) =>
        (p.effectiveDate ?? "").localeCompare(newest) > 0
          ? (p.effectiveDate ?? "")
          : newest,
      "",
    );
  return (policy.effectiveDate ?? "") === newestSiblingDate;
}

function normalizePolicyNumber(policyNumber: string | null | undefined): string {
  return (policyNumber ?? "").trim().toUpperCase();
}

export type PolicySyncFanoutItem = {
  policyNumber: string;
  skipped?: boolean;
  skipReason?: PolicySyncIneligibleReason;
  error?: string;
};

/**
 * CSR-2169 — the activity-log line for a whole-insured refresh: what was
 * synced, what was skipped and why, and what failed to start — so the fan-out
 * never silently syncs fewer policies than the insured has.
 * e.g. `Synced 2 policies. Skipped 3: older terms (A, B), carrier without
 * automatic sync (C). 1 failed to start (D).`
 */
export function buildInsuredRefreshSyncSummary(
  items: PolicySyncFanoutItem[],
): string {
  const queuedCount = items.filter((i) => !i.skipped && !i.error).length;
  const skipped = items.filter((i) => i.skipped);
  const errored = items.filter((i) => !i.skipped && i.error);
  const reasonGroups: string[] = [];
  const olderTerms = skipped
    .filter((i) => i.skipReason === "older_term")
    .map((i) => i.policyNumber);
  if (olderTerms.length > 0) {
    reasonGroups.push(`older terms (${olderTerms.join(", ")})`);
  }
  const unsupportedCarriers = skipped
    .filter((i) => i.skipReason === "unsupported_carrier")
    .map((i) => i.policyNumber);
  if (unsupportedCarriers.length > 0) {
    reasonGroups.push(
      `carrier without automatic sync (${unsupportedCarriers.join(", ")})`,
    );
  }
  const parts = [
    `Synced ${queuedCount} ${queuedCount === 1 ? "policy" : "policies"}.`,
  ];
  if (skipped.length > 0) {
    parts.push(`Skipped ${skipped.length}: ${reasonGroups.join(", ")}.`);
  }
  if (errored.length > 0) {
    parts.push(
      `${errored.length} failed to start (${errored.map((i) => i.policyNumber).join(", ")}).`,
    );
  }
  return parts.join(" ");
}
